import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";

import { getAuthenticationToken } from "src/auth/helpers";
import {
  SCHEMA_NAME_PATTERN,
  STUDIO_API_URLS,
  type SchemaFolder,
} from "src/views/admin/formStudio/constants";

export const SCHEMA_FETCH_TIMEOUT_MS = 30_000;

interface SchemaFetchResponseBody {
  name?: string;
  folder?: string;
  schema?: CloudscapeJsonSchema;
}

/**
 * Fetches a schema by name from the FinTech Cloudscape JSON Forms Studio API.
 *
 * Validation failures throw synchronously so callers cannot accidentally
 * issue a network request with a malformed name. All other failure modes
 * (timeout, network error, non-2xx response, malformed body) reject the
 * returned promise with an error whose message identifies the schema name.
 */
export function fetchSchema(
  name: string,
  folder: SchemaFolder,
  externalSignal?: AbortSignal
): Promise<CloudscapeJsonSchema> {
  if (!SCHEMA_NAME_PATTERN.test(name)) {
    throw new Error(
      `Invalid schema name "${name}". Schema names must match ${SCHEMA_NAME_PATTERN}.`
    );
  }

  return doFetchSchema(name, folder, externalSignal);
}

async function doFetchSchema(
  name: string,
  folder: SchemaFolder,
  externalSignal?: AbortSignal
): Promise<CloudscapeJsonSchema> {
  const url = `${STUDIO_API_URLS.getUrl}/${encodeURIComponent(
    name
  )}?folder=${encodeURIComponent(folder)}`;

  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, SCHEMA_FETCH_TIMEOUT_MS);

  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener("abort", onExternalAbort);
    }
  }

  try {
    const token = await getAuthenticationToken();
    if (!token) {
      throw new Error(
        `Schema fetch for "${name}" failed: authentication token is unavailable.`
      );
    }
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(
        `Schema fetch for "${name}" failed: HTTP ${response.status}.`
      );
    }

    const body = (await response.json()) as SchemaFetchResponseBody;
    if (!body || typeof body !== "object" || !body.schema) {
      throw new Error(
        `Schema fetch for "${name}" returned a malformed response.`
      );
    }

    return body.schema;
  } catch (err: unknown) {
    if (timedOut) {
      throw new Error(
        `Schema fetch for "${name}" timed out after ${SCHEMA_FETCH_TIMEOUT_MS}ms.`
      );
    }
    if (err instanceof Error && err.name === "AbortError") {
      throw err;
    }
    if (err instanceof TypeError) {
      throw new Error(`Schema fetch for "${name}" failed: network error.`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) {
      externalSignal.removeEventListener("abort", onExternalAbort);
    }
  }
}
